import { Option } from "../types/Quote.interface";
import { Select } from "@/shared/components/ui/Select";
import { RadioButtonGroup } from "@/shared/components/ui/RadioButtonGroup";
import { Label } from "@/shared/components/ui/label";
import kebabCase from "lodash/kebabCase";
import { cn } from "../utils";
import { ReimbursementTipVariant } from "./CoverageEditor/CoverageParameterControls";

export type CustomControlType = "" | "buttons";

interface DynamicOptionsFieldProps {
    id: string;
    options: Option[];
    initialValue?: string;
    onValueChange: (value: string) => void;
    label?: string;
    ariaLabelledBy?: string;
    toggleLimit?: number;
    constrainWidthToTrigger?: boolean;
    labelStyles?: string;
    variant?: "default" | "floating" | "floating-inner" | "icon";
    customControlType?: CustomControlType;
    radioGroupItemClassName?: string;
    reimbursementVariant?: ReimbursementTipVariant;
    showReimbursementTip?: boolean;
}

// Helper component for the single-option case for better readability
const StaticOptionDisplay = ({ option }: { option: Option }) => (
    <div className="flex">
        <span className="rounded-md bg-background-brand-b-secondary p-1 px-2 text-2xl font-semibold text-content-primary">{option?.label}</span>
    </div>
);

/**
 * Renders a Select, RadioButtonGroup, or static content based on the number of options and props.
 * This component is purely presentational and expects pre-formatted options.
 */
function DynamicOptionsField(props: DynamicOptionsFieldProps) {
    const {
        id,
        options,
        label,
        ariaLabelledBy,
        initialValue,
        onValueChange,
        toggleLimit = 5,
        constrainWidthToTrigger = false,
        variant,
        customControlType = "",
        radioGroupItemClassName,
        reimbursementVariant,
        showReimbursementTip
    } = props;

    const labelId = label ? `${kebabCase(label)}-label` : undefined;
    const effectiveLabelledBy = ariaLabelledBy ?? labelId;
    const optionsLength = options?.length ?? 0;

    const renderControl = () => {
        // Case 1: No options to display
        if (optionsLength === 0) {
            return null; // Nothing to render
        }

        // Case 2: A single, static option
        if (optionsLength === 1 && options[0]) {
            return <StaticOptionDisplay option={options[0]} />;
        }

        // Case 3: Forced button display
        if (customControlType === "buttons") {
            return <RadioButtonGroup variant="buttons" initialValue={initialValue} onValueChange={onValueChange} options={options} ariaLabelledBy={effectiveLabelledBy} />;
        }

        // Case 4: Radio buttons for a small number of options
        if (optionsLength > 1 && optionsLength <= toggleLimit) {
            return (
                <RadioButtonGroup
                    variant={variant}
                    initialValue={initialValue}
                    onValueChange={onValueChange}
                    options={options}
                    radioGroupItemClassName={radioGroupItemClassName}
                    showReimbursementTip={showReimbursementTip}
                    reimbursementVariant={reimbursementVariant}
                    ariaLabelledBy={effectiveLabelledBy}
                />
            );
        }

        // Case 5: A dropdown for a large number of options
        if (optionsLength > toggleLimit) {
            return (
                <Select
                    variant={variant === "floating-inner" ? "floating-inner" : "default"}
                    value={initialValue}
                    onValueChange={onValueChange}
                    options={options}
                    className="h-16 flex-1 py-3"
                    constrainWidthToTrigger={constrainWidthToTrigger}
                    triggerProps={{ id, "aria-labelledby": effectiveLabelledBy }}
                />
            );
        }

        return null;
    };

    return (
        <div className="flex w-full flex-col gap-2">
            {label && (
                <Label id={labelId} data-testid={labelId} htmlFor={id} className={cn("bg-background-transparent text-sm font-semibold text-content-primary", props.labelStyles)}>
                    {label}
                </Label>
            )}
            {renderControl()}
        </div>
    );
}

export default DynamicOptionsField;
